import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Music, PenLine, Building2, PieChart, UploadCloud, ArrowUpRight } from "lucide-react";

const CARDS = [
  { key: "songs", label: "Songs", icon: Music, to: "/songs" },
  { key: "writers", label: "Writers", icon: PenLine, to: "/writers" },
  { key: "publishers", label: "Publishers", icon: Building2, to: "/publishers" },
  { key: "splits", label: "Ownership Splits", icon: PieChart, to: "/songs" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/catalog/health").then(({ data }) => setHealth(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Overview</h4>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase">Dashboard</h1>
        </div>
        <button
          onClick={() => navigate("/upload")}
          data-testid="dashboard-import-btn"
          className="flex items-center gap-2 bg-primary text-primary-foreground font-bold px-5 py-2.5 rounded-md hover:bg-primary/90 transition-colors"
        >
          <UploadCloud className="h-4 w-4" /> Import Catalog
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        {CARDS.map(({ key, label, icon: Icon, to }) => (
          <button
            key={key}
            onClick={() => navigate(to)}
            data-testid={`stat-${key}`}
            className="group text-left bg-card border border-border rounded-lg p-6 hover:border-primary/40 hover:-translate-y-1 transition-transform"
            style={{ transition: "transform 0.2s ease, border-color 0.2s ease" }}
          >
            <div className="flex items-center justify-between mb-6">
              <Icon className="h-5 w-5 text-primary" />
              <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-primary transition-colors" />
            </div>
            <div className="font-mono text-4xl font-semibold tracking-tight">
              {stats ? stats[key] : "—"}
            </div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-2">{label}</div>
          </button>
        ))}
      </div>

      {health && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="bg-card border border-border rounded-lg p-6 sm:p-8" data-testid="catalog-health">
            <h3 className="font-heading text-lg font-semibold mb-5">Catalog Health</h3>
            <div className="flex items-center gap-6 mb-5">
              <div><div className="font-mono text-4xl font-semibold text-primary">{health.total_songs ? Math.round((health.complete / health.total_songs) * 100) : 100}%</div><div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Complete</div></div>
              <div className="h-12 w-px bg-border" />
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <span className="text-zinc-400">Missing ISRC</span><span className="font-mono text-right">{health.missing_isrc}</span>
                <span className="text-zinc-400">Missing ISWC</span><span className="font-mono text-right">{health.missing_iswc}</span>
                <span className="text-zinc-400">Split disputes</span><span className="font-mono text-right">{health.split_disputes}</span>
                <span className="text-zinc-400">Unregistered</span><span className="font-mono text-right">{health.unregistered_works}</span>
              </div>
            </div>
            <button onClick={() => navigate("/ai")} data-testid="fix-issues-btn" className="text-primary text-sm font-semibold hover:underline">Resolve with AI Assistant →</button>
          </div>
          <div className="bg-card border border-border rounded-lg p-6 sm:p-8">
            <h3 className="font-heading text-lg font-semibold mb-5">Rights Coverage</h3>
            <div className={`font-mono text-4xl font-semibold ${health.rights_coverage === "Worldwide" ? "text-primary" : "text-amber-400"}`}>{health.rights_coverage}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1 mb-4">{health.territories?.length || 0} territories registered</div>
            <button onClick={() => navigate("/rights")} data-testid="view-rights-btn" className="text-primary text-sm font-semibold hover:underline">Open Rights Map →</button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6 sm:p-8">
        <h3 className="font-heading text-xl font-semibold tracking-tight mb-6">Recent Songs</h3>
        {stats?.recent_songs?.length ? (
          <div className="divide-y divide-border">
            {stats.recent_songs.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/songs/${s.id}`)}
                data-testid={`recent-song-${s.id}`}
                className="w-full flex items-center justify-between py-4 text-left hover:bg-secondary/40 px-2 rounded-md transition-colors"
              >
                <div>
                  <div className="font-semibold">{s.title}</div>
                  <div className="font-mono text-xs text-zinc-500 mt-1">
                    ISRC {s.isrc || "—"} · ISWC {s.iswc || "—"}
                  </div>
                </div>
                <div className="text-xs text-zinc-500">
                  {(s.writer_splits?.length || 0) + (s.publisher_splits?.length || 0)} splits
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Music className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 mb-4">No songs yet. Import a catalog or add one manually.</p>
            <button
              onClick={() => navigate("/upload")}
              data-testid="empty-import-btn"
              className="text-primary font-semibold hover:underline"
            >
              Import your first catalog →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
